<template>
  <div class="card p-10">
    <RequiredText/>
    
    <form id="estimateForm" @submit.prevent="submitForm">
      <Card class="card p-5">
        <div class="d-flex flex-column flex-md-row">
          <div class="mb-3 mb-md-0 me-md-5 d-flex justify-content-center align-items-center">
            <img v-if="selectedContact && selectedContact.avatar" 
                :src="selectedContact.avatar_url" 
                class="img-fluid rounded-3" 
                style="width: 150px; height: 150px; border-radius: 10px;" />
            <div v-if="selectedContact && !selectedContact.avatar" class="cursor-pointer symbol symbol-30px symbol-md-40px mb-6 d-flex justify-content-center align-items-center">
              <span class="symbol-label fs-2x fw-bold text-primary bg-light-primary d-flex align-items-center justify-content-center" 
                    style="width: 150px; height: 150px; border-radius: 10px;">
                {{ selectedContact.lastname ? selectedContact.lastname.charAt(0).toUpperCase() : "" }}
                {{ selectedContact.firstname ? selectedContact.firstname.charAt(0).toUpperCase() : "" }}
              </span>
            </div>
            <img v-if="!selectedContact" 
                src="/assets/media/avatars/blank.png" 
                class="img-fluid rounded-3" 
                style="width: 150px; height: 150px; border-radius: 10px;" />
          </div>

          <!-- Form Section -->
          <div class="flex-grow-1">
            <div class="row g-3">
              <Select2
                container-class="form-group col-12 col-md-6  col-lg-6"
                :multiple="false"
                :show-search="true"
                :selectClass="'form-select form-control-sm form-select-solid'"
                :name="`estimate-contact`"
                :label="$t('invoice.label.customer')"
                labelClass="form-label fs-6 text-black"
                :placeholder-text="$t('invoice.label.customer')"
                v-model="formData.contact_id"
                :IsInputGroup="$can(abilities.CREATE_CONTACT)"
                icon="bi bi-plus"
                :clickAddon="openSupplierModal">
                <option v-for="contact in contacts" :value="contact.id" :key="contact.id">
                  {{ contact.lastname }} {{ contact.firstname }}
                </option>
              </Select2>

              <InputDate
                containerClass="col-12 col-md-3 col-lg-3"
                name="estimate-date"
                type="text"
                :label="$t('invoice.label.date')"
                labelClass="form-label fs-6 text-black"
                :inputClass="'form-control form-control-sm form-control-solid'"
                :isRequired="true" 
                size="lg"
                :placeholder="$t('invoice.label.date')"
                v-model="formData.estimate_date" />

              <InputDate
                containerClass="col-12 col-md-3 col-lg-3"
                name="estimate-delivery-date"
                type="text"
                :label="$t('invoice.label.deliveryDate')"
                labelClass="form-label fs-6 text-black"
                :inputClass="'form-control form-control-sm form-control-solid'"
                size="lg"
                :placeholder="$t('invoice.label.deliveryDate')"
                :isRequired="true"
                :min-date="formData.estimate_date"
                v-model="formData.due_date" />

              <Select2
                container-class="col-12 col-md-3 col-lg-3"
                :multiple="false"
                :show-search="true"
                :selectClass="'form-select form-control-sm form-select-solid'"
                name="estimate-payment-type"
                :label="$t('invoices.label.paymentType')"
                labelClass="form-label fs-6 text-black"
                :isRequired="true"
                :placeholderText="$t('invoices.label.paymentType')"
                v-model="formData.payment_type_id">
                <option v-for="paymentType in paymentTypes" :value="paymentType.id" :key="paymentType.id">
                  {{ paymentType.name }}
                </option>
              </Select2>

              <Select2
                  containerClass="col-12 col-md-3 col-lg-3"
                :multiple="false"
                :show-search="true"
                :selectClass="'form-select form-control-sm form-select-solid'"
                name="estimate-currency"
                :label="$t('invoice.label.currency')"
                labelClass="form-label fs-6 text-black"
                :isRequired="true"
                :placeholderText="$t('invoice.label.currency')"
                v-model="formData.currency_id">
                <option v-for="(currency, index) in currencies" :key="index" :value="currency.id">
                  {{ currency.name + ' (' +  currency?.iso + ')'}}
                </option>
              </Select2>

              <Input
                v-show="discountCurrencyIso !== companyCurrency?.iso"
                containerClass="col-12 col-md-3 col-lg-3"
                name="estimate-exchange"
                type="text"
                :label="$t('invoice.label.exchange')"
                labelClass="form-label fs-6 text-black"
                :inputClass="'form-control form-control-sm form-control-solid'"
                :isRequired="true" 
                size="lg"
                :placeholder="$t('invoice.placeholder.exchange')"
                v-model="formData.exchange_rate"
                :IsInputGroup="true"
                addon-class="border-0 border-end"
                iconDir="left"
                :iconText="'1 '+ discountCurrencyIso +' ='">
                <template #rightAddon>
                  <span class="position-absolute end-0 top-50 translate-middle-y me-3 text-muted fw-bolder">
                    {{ companyCurrency?.iso }}
                  </span>
                </template>
              </Input>
            </div>
          </div>
        </div>
      </Card>


      <Card class="card my-5 p-5">
        <div class="flex-column current">
          <div class="table-responsive ">
            <table class="table table-flush table-borderless table-head-custom table-vertical-center align-middle gy-7 gx-3"
                   id="estimate-items-datatable">
              <tbody>
              <tr>
                <th style="width: 450px" class="fw-bolder required">
                  {{ $t('invoice.label.item.name') }}
                </th>
                <th style="min-width: 110px" class="fw-bolder required">{{ $t('invoice.label.item.quantity') }}</th>
                <th style="min-width: 180px" class="fw-bolder required">{{ $t('invoice.label.item.price') }}</th>
                <th style="min-width: 180px" class="fw-bolder">{{ $t('invoice.label.item.discount') }}</th>
                <th style="min-width: 400px" class="fw-bolder">{{ $t('invoice.label.item.tax') }}</th>
                <th style="min-width: 180px" class="fw-bolder">{{ $t('invoice.label.item.amount') }}</th>
                <th class="fw-bolder" style="text-align: end !important;"></th>
              </tr>
              <EstimateFormItem
                  v-for="(item, index) in formData.items"
                  :key="item.product_id"
                  :index="index"
                  :item="item"
                  :symbol="discountCurrencyIso"
                  :selectedItems="selectedItemIds"
                  :rate="formData.exchange_rate"
                  @delete-item="removeItemRow"/>
              <tr>
                <td colspan="7">
                  <div class="justify-content-end align-items-end float-end">
                    <a class="btn btn-primary" @click="addItemRow">
                      {{ $t('invoice.addItem') }}
                    </a>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <div class="row">
        <div class="col-md-6">
          <Card class="card h-100 p-5">
            <CKEditor
                labelClass="form-label fs-6 text-black"
                :label="$t('crm.estimate.label.addNote')"
                id="estimate-editor"
                :content="formData.notes"
                @text="getNote"
            />
          </Card>
        </div>

        <div class="col-md-6">
          <Card class="card p-5">
            <div class="row">
              <div class="border-bottom border-gray-300">
                <div class="d-flex justify-content-between mb-3">
                  <span class="font-weight-600">{{ $t('invoice.subTotal') }} :</span>
                  <span>{{ $formatLocalAmount(parseFloat(estimateSubTotal)) }} {{ discountCurrencyIso }}</span>
                </div>
                <div class="d-flex justify-content-between mb-3">
                  <span class="font-weight-600">{{ $t('invoice.label.totalDiscount') }} :</span>
                  <span>{{ $formatLocalAmount(parseFloat(estimateDiscountTotal)) }} {{ discountCurrencyIso }}</span>
                </div>
              </div>
              <div class="border-bottom border-gray-300 mt-3">
                <div class="d-flex justify-content-between text-warning mb-3">
                  <span class="font-weight-600">{{ $t('invoice.totalHT') }}</span>
                  <span>{{ $formatLocalAmount(parseFloat(estimateTotalHt)) }} {{ discountCurrencyIso }}</span>
                </div>
                <div class="d-flex justify-content-between mb-3">
                  <span class="font-weight-600">{{ $t('invoice.totalTaxes') }} :</span>
                  <span>{{ $formatLocalAmount(parseFloat(estimateTaxesTotal)) }} {{ discountCurrencyIso }}</span>
                </div>
              </div>
              <div class="mt-3">
                <div class="d-flex justify-content-between text-warning">
                  <span class="font-weight-600">{{ $t('invoice.totalTtc') }}</span>
                  <span>{{ $formatLocalAmount(parseFloat(estimateTotalTtc)) }} {{ discountCurrencyIso }}</span>
                </div>
              </div>
            </div>
            <div class="row align-content-end justify-content-end mt-7">
              <div class="col-12 text-primary text-end">
                <span class="fw-bolder fs-2">{{ $t('invoice.total') }}</span>
                <br>
                <span class="fw-bolder fs-3x"> {{ $formatLocalAmount(parseFloat(estimateTotal)) }} {{ discountCurrencyIso }} </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-10">
        <Button type="cancel" btn-class="btn-sm me-2" btn-text="button.cancel" @click.prevent="cancel" size="7px 30px"></Button>
        <Button type="submit" btn-class="btn-sm me-2" btn-text="button.save" size="7px 30px" :isDisabled="submitting" :isLoading="submitting"></Button>
      </div>
    </form>

    <CreateTax @load="afterCreateTax"  id="taxCreateModal"/>

    <div class="modal fade" tabindex="-1" id="contactCreateModal">
      <div class="modal-dialog modal-xl">
        <CreateContact id="contactCreateModal"
                       :get-countries="countries"
                       :get-currencies="currencies"
                       :contact="formCustomerData"
                       :modal="true"
                       @success="afterCreateNewContact"/>
      </div>
    </div>

  </div>
</template>

<script setup>

import CreateTax from "@/components/Tax/CreateTax.vue";
import CreateContact from "@/components/contact/CreateContact.vue";
import {useStore} from "vuex";
import {computed, defineProps, watch, ref, onMounted} from "vue";
import {BASE_IMAGE_URL} from "@/services/api";
import InputDate from "@/components/InputDate.vue";
import Select2 from "@/components/Select2.vue";
import Input from "@/components/Input.vue";
import Card from "@/Modules/CRM/components/Card.vue";
import {useDiscountType} from "@/composables/discountType";
import EstimateFormItem from "@/Modules/CRM/components/Estimate/EstimateFormItem.vue";
import {$formatLocalAmount, addDay, formatDate} from "@/mixins/Helpers";
import CKEditor from "@/components/CKEditor.vue";
import Button from "@/components/Button.vue";
import { useRouter } from 'vue-router';
import RequiredText from "@/components/RequiredText.vue"
import abilities from "@/composables/abilities";

const {discountType} = useDiscountType()

const store = useStore()
const router = useRouter()

const props = defineProps({
  estimate: {
    type: Object,
    default: () => ({
      estimate_date: new Date(),
      items: [{
        product_id: '',
        quantity: 1,
        price: 0,
        description: '',
        discount_type: 'percent',
        discount_value: 0,
        taxes: [],
        total: 0
      }],
      taxes: [],
      estimate_discount_type:  'percent',
      estimate_discount_value:  0,
      notes: '',
      contact_id: '', 
      currency_id: '',
      opportunity_id: '',
      payment_type_id: '',
    })
  },
  submitForm: Function,
  submitting: {type: Boolean, default: false}
})

const formData = ref(props.estimate)

const formCustomerData = ref({
  id: null,
  gender: null,
  contactType: 'particular',
  lastname: null,
  firstname: null,
  address: {
    street: null,
    street2: null,
    city: null,
    zipcode: null,
    country: null
  },
  customer: false,
  birthday: null,
  supplier: true,
  propspect: false,
  telephone: [],
  previewAvatar: BASE_IMAGE_URL + '/assets/blank.png',
  email: null,
  website: null
})

const contacts = computed(() => store.getters['contacts/getContacts'])
const taxes = computed(() => store.getters['taxes/getTaxes'])
const countries = computed(() => store.getters['countries/getCountries'])
const currencies = computed(() => store.getters['currencies/getCurrencies'])
const paymentConditions = computed(() => store.getters['payments/paymentConditions'])
const companyCurrency = computed(() => store.getters['company/getCompanyCurrency'])
const paymentTypes = computed(() => store.getters['payments/paymentTypes'])
const selectedContact = computed(() => {
  return contacts.value.find(contact => contact.id === formData.value.contact_id);
})

const selectedItemIds = computed(() => formData.value.items.map(el => el.product_id))
const estimateSubTotal = computed(() => formData.value.items?.reduce((acc, cur) => acc += cur.price * cur.quantity, 0))

const estimateDiscountTotal = computed(() => {
  let itemDiscounts = 0
  formData.value.items.filter((item) => {
    if(item.discount_value){
      let discount = applyDiscount(item.discount_type, item.discount_value, item.price * item.quantity)
      itemDiscounts += parseFloat(discount)
    }
  });
  return itemDiscounts
})
const estimateTotalHt = computed(() => parseFloat(estimateSubTotal.value) - parseFloat(estimateDiscountTotal.value))

const estimateTaxesTotal = computed(() => {
  let itemTaxes = 0
  formData.value.items.filter((item) => {
    let total = 0
    const orderTaxesSelected = taxes.value.filter(tax => item.taxes?.includes(tax.id))

    let discountItem = applyDiscount(item.discount_type, item.discount_value, item.price * item.quantity)
    let estimateItemTotal = item.price * item.quantity - discountItem

    if (orderTaxesSelected.length > 0) {
      const totalTaxesPrice = orderTaxesSelected.reduce((accumulator, tax) => {
        if (tax.type === 'amount') {
          accumulator.accAmount += parseFloat(tax.amount);
        } else {
          accumulator.accPercent += (estimateItemTotal * parseFloat(tax.amount) / 100);
        }
        return accumulator
      }, { accAmount: 0, accPercent: 0 })
      total = totalTaxesPrice.accAmount + totalTaxesPrice.accPercent
    }
    itemTaxes += parseFloat(total)
  });

  return itemTaxes
})

const estimateTotalTtc = computed(() => {
  return parseFloat(estimateTotalHt.value) + parseFloat(estimateTaxesTotal.value)
})

const estimateTotal = computed(() => {
  return estimateSubTotal.value + estimateTaxesTotal.value - estimateDiscountTotal.value
})

const discountCurrencyIso = computed(() => {
  if(!formData.value.currency_id) {
    return  discountType.value.fixed.symbol
  }
  return currencies.value.find(el => el.id === formData.value.currency_id)?.iso
})

const updateCurrencyId = () => {
  if (selectedContact.value && selectedContact.value.currency_id) {
    formData.value.currency_id = selectedContact.value.currency_id;
  } else {
    formData.value.currency_id = null;
  }
};

watch(() => formData.value.contact_id, (newContactId) => {
  updateCurrencyId();
});

watch(() => formData.value.estimate_date, () => {
  let date = addDay(formData.value.estimate_date, 30)
  formData.value.due_date = date
})


function cancel(){
  router.push({ name: 'estimates'})
}

function applyDiscount (discountType, discountVal, amount) {
  if (discountType !== 'amount') {
    return (amount * parseFloat(discountVal || 0 ) / 100)
  }
  return parseFloat(discountVal) || 0
}

function openSupplierModal() {
  $('#contactCreateModal').modal('toggle')
}

function addItemRow() {
  formData.value.items.push({
    product_id: '',
    quantity: 1,
    price: 0,
    description: '',
    discount_type: 'percent',
    discount_value: 0,
    taxes: [],
    total: 0
  })
}

function removeItemRow(index) {
  if (formData.value.items.length > 1) {
    formData.value.items.splice(index, 1)
  }
}

function afterCreateTax(tax) {
  $("#taxCreateModal").modal('toggle')
  formData.value.taxes.push(tax?.id)
}

function afterCreateNewContact(contact){
  reloadContacts()
  formData.value.contact_id = contact.id
  formCustomerData.value = {
    id: null,
    gender: null,
    contactType: 'particular',
    lastname: null,
    firstname: null,
    address: {
      street: null,
      street2: null,
      city: null,
      zipcode: null,
      country: null
    },
    customer: false,
    birthday: null,
    supplier: true,
    telephone: [],
    previewAvatar: BASE_IMAGE_URL + '/assets/blank.png',
    email: null,
    website: null
  }
}

async function reloadContacts() {
  await store.dispatch('contacts/loadContacts')
}

function getNote(text) {
  formData.value.notes = text
}

onMounted(() => {
  store.dispatch('currencies/loadCurrencies')
  store.dispatch('countries/loadCountries')
})
</script>

<style scoped>

</style>
